/**
 * Helpers de manipulacao da lista de campos no Editor.
 *
 * Funcoes puras — chaves estaveis, criacao de campo novo a partir de um tipo,
 * normalizacao do payload antes de POST /campos e as operacoes que mexem em
 * mais de um campo (excluir, arrastar). O payload leva so os limites de
 * validacao do tipo atual (FORM-39).
 *
 * Identidade (FORM-09): no Editor todo campo tem `_key`, e as regras de
 * condicao citam a `_key` — campo recem-criado ainda nao tem id, e a regra que
 * citava `c.id` gravava o rotulo no lugar do UUID. O payload leva a `_key` de
 * cada campo; o backend troca pela UUID final ao gravar a condicao.
 */
import { tipoSemInput, tipoTemOpcoes } from "../../services/formularios";

import { textoDaCondicao } from "./condicoes";
import { limitesParaPayload } from "./limitesCampo";
import { sugerirDestino } from "./mapeamentoCampo";

let _seq = 0;

/** Gera uma chave temporaria unica para campos ainda sem id. */
export function novaChaveTemp() {
  _seq += 1;
  return `tmp-${_seq}`;
}

function chaveDoId(id) {
  return `id-${id}`;
}

function regrasDa(condicao) {
  return condicao && Array.isArray(condicao.regras) ? condicao.regras : [];
}

// Regra do servidor cita UUID; no Editor passa a citar a `_key`. Referencia
// que ja e' `_key` (ou que nao casa com ninguem) fica como esta.
function condicaoComChaves(condicao, chavePorId) {
  const regras = regrasDa(condicao);
  if (regras.length === 0) return condicao;
  return {
    ...condicao,
    regras: regras.map((regra) => {
      const chave = chavePorId.get(String(regra.campo));
      return chave ? { ...regra, campo: chave } : regra;
    }),
  };
}

/**
 * Garante `_key` estavel em cada campo — `id-<uuid>` para o que ja existe no
 * servidor, chave temporaria para o novo — e troca as referencias das regras
 * de UUID para `_key`. Idempotente: reusa a `_key` ja presente.
 */
export function comChaves(campos) {
  const lista = (Array.isArray(campos) ? campos : []).map((c) => ({
    ...c,
    _key: c._key || (c.id != null ? chaveDoId(c.id) : novaChaveTemp()),
    opcoes: Array.isArray(c.opcoes) ? c.opcoes : [],
  }));
  const chavePorId = new Map(
    lista.filter((c) => c.id != null).map((c) => [String(c.id), c._key])
  );
  return lista.map((c) => ({ ...c, condicao: condicaoComChaves(c.condicao, chavePorId) }));
}

/**
 * Destinos ja usados pelos OUTROS campos: `{destino: "Rotulo do campo"}`.
 *
 * Base da recusa de destino repetido (FORM-23) e da sugestao do campo novo
 * (FORM-22). O primeiro campo de cada destino e quem fica no mapa — e o que o
 * servidor cita na mensagem de erro.
 */
export function destinosUsadosPorOutros(campos, chave = null) {
  const usados = {};
  (Array.isArray(campos) ? campos : []).forEach((campo) => {
    if (campo._key === chave) return;
    const destino = campo.mapeia_para;
    if (!destino || usados[destino]) return;
    usados[destino] = campo.label || "Campo sem rótulo";
  });
  return usados;
}

/**
 * Cria um campo novo (rascunho) para um dado tipo. `destinosUsados` (ver
 * `destinosUsadosPorOutros`) faz o campo de e-mail e o de telefone ja
 * nascerem mapeados quando o destino esta livre (FORM-22) — o gestor troca no
 * painel se quiser.
 */
export function novoCampo(tipo, ordem, destinosUsados = {}) {
  return {
    _key: novaChaveTemp(),
    id: undefined, // sem id -> backend cria
    ordem,
    tipo,
    label: "",
    placeholder: "",
    ajuda: "",
    obrigatorio: false,
    opcoes: tipoTemOpcoes(tipo) ? ["Opção 1"] : [],
    mapeia_para: sugerirDestino(tipo, destinosUsados),
    validacao: {},
    largura: "FULL",
    condicao: {},
  };
}

/**
 * Normaliza a lista para o contrato do backend (POST /campos): reaplica
 * `ordem` pela posicao atual, manda a `_key` de todos (referencia das regras)
 * e zera campos inaplicaveis por tipo (opcoes em campos sem opcoes, etc).
 * Condicao sem regra vai como `{}`: o servidor recusa `regras: []`.
 */
export function paraPayload(campos) {
  return (Array.isArray(campos) ? campos : []).map((c, idx) => {
    const ehSecao = tipoSemInput(c.tipo);
    const out = {
      _key: c._key,
      ordem: idx,
      tipo: c.tipo,
      label: String(c.label || "").trim(),
      placeholder: ehSecao ? "" : String(c.placeholder || ""),
      ajuda: String(c.ajuda || ""),
      obrigatorio: ehSecao ? false : Boolean(c.obrigatorio),
      opcoes: tipoTemOpcoes(c.tipo)
        ? (Array.isArray(c.opcoes) ? c.opcoes : [])
            .map((o) => String(o).trim())
            .filter(Boolean)
        : [],
      mapeia_para: c.mapeia_para || "",
      validacao: limitesParaPayload(c.tipo, c.validacao),
      largura: c.largura === "HALF" ? "HALF" : "FULL",
      condicao: regrasDa(c.condicao).length > 0 ? c.condicao : {},
    };
    // So inclui id quando ja existe (backend faz upsert).
    if (c.id != null) out.id = c.id;
    return out;
  });
}

/**
 * Ids devolvidos pelo "Salvar campos" aplicados ao canvas (FORM-04).
 *
 * Sem isso o campo criado na sessao seguia sem id, e o proximo salvamento o
 * recriava com UUID novo — as respostas recebidas no meio sumiam. `salvos`
 * vem na ordem de `enviados`; o que foi editado ou criado enquanto a
 * requisicao corria continua como esta, so ganhando o id.
 */
export function reidratarIds(atuais, enviados, salvos) {
  if (!Array.isArray(salvos) || salvos.length !== enviados.length) {
    throw new Error("O servidor devolveu os campos num formato inesperado. Recarregue a página.");
  }
  const idPorChave = new Map(enviados.map((c, i) => [c._key, salvos[i].id]));
  return atuais.map((c) => (idPorChave.has(c._key) ? { ...c, id: idPorChave.get(c._key) } : c));
}

/**
 * Exclui o campo `chave` e as regras que o citam (FORM-09).
 *
 * Regra pendente escondia o campo condicional para sempre na pagina publica.
 * Condicao que fica sem regra vira `{}` (o campo passa a aparecer sempre).
 * `afetados` sao os rotulos dos campos que perderam regra, para o aviso.
 */
export function removerCampo(campos, chave) {
  const afetados = [];
  const restantes = campos
    .filter((c) => c._key !== chave)
    .map((c) => {
      const regras = regrasDa(c.condicao);
      const mantidas = regras.filter((regra) => regra.campo !== chave);
      if (mantidas.length === regras.length) return c;
      afetados.push(c.label || "Campo sem rótulo");
      return {
        ...c,
        condicao: mantidas.length > 0 ? { ...c.condicao, regras: mantidas } : {},
      };
    });
  return { campos: restantes, afetados };
}

// Chaves que a condicao de `campo` cita, direta ou transitivamente.
function dependenciasDe(campo, porChave, vistos = new Set()) {
  regrasDa(campo?.condicao).forEach((regra) => {
    if (vistos.has(regra.campo)) return;
    vistos.add(regra.campo);
    dependenciasDe(porChave.get(regra.campo), porChave, vistos);
  });
  return vistos;
}

/**
 * Campos que a condicao do campo `chave` pode citar: qualquer outro campo
 * com input, antes ou depois dele na lista, desde que nao feche ciclo — o
 * backend aceita referencia para frente e recusa so o ciclo (FORM-09). Um
 * campo cuja condicao ja depende deste (mesmo por tabela) fica de fora, senao
 * A dependeria de B e B de A, e nenhum dos dois apareceria.
 */
export function camposCitaveis(campos, chave) {
  const porChave = new Map(campos.map((c) => [c._key, c]));
  return campos.filter(
    (c) =>
      c._key !== chave
      && !tipoSemInput(c.tipo)
      && !dependenciasDe(c, porChave).has(chave)
  );
}

/**
 * Texto que muda quando muda algo que o "Salvar campos" grava (FORM-31).
 * O id fica de fora: ganhar o UUID depois de salvar nao e' alteracao.
 */
export function assinaturaCampos(campos) {
  return JSON.stringify(paraPayload(campos).map(({ id: _id, ...resto }) => resto));
}

/**
 * Opcoes sem vazias e sem repetidas (FORM-40), comparando como a condicao
 * compara: sem caixa e sem espacos nas pontas. Com "Cozinha" duas vezes, a
 * caixa de selecao marcava as duas juntas e o Dashboard dava toda a contagem
 * a primeira. `repetidas` sao as que sairam, para o aviso.
 */
export function semOpcoesRepetidas(opcoes) {
  const vistas = new Set();
  const unicas = [];
  const repetidas = [];
  (Array.isArray(opcoes) ? opcoes : []).forEach((opcao) => {
    const texto = String(opcao ?? "").trim();
    if (!texto) return;
    const chave = textoDaCondicao(texto);
    if (vistas.has(chave)) {
      repetidas.push(texto);
      return;
    }
    vistas.add(chave);
    unicas.push(texto);
  });
  return { opcoes: unicas, repetidas };
}
